import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle, 
  Clock,
  RefreshCw,
  Mail,
  MessageSquare,
  Send,
  X,
  AlertTriangle,
  User
} from 'lucide-react';
import { fetchSupportTickets, replySupportTicket, SupportTicket } from '../services/api.ts';
import { useAuth } from '../context/AuthContext.tsx';
import './Support.css';

const Support: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const ticketsPerPage = 10;

  const adminEmail = user?.email || '';

  console.log('🎯 Support component rendered');

  useEffect(() => {
    console.log('🚀 useEffect triggered - loading tickets');
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      console.log('🔄 Loading support tickets...');
      setLoading(true);
      const data = await fetchSupportTickets();
      console.log('✅ Received support tickets:', data);
      setTickets(data);
      setError(null);
    } catch (err) {
      console.error('❌ Error loading tickets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filterStatus === 'all') return true;
    return ticket.status === filterStatus;
  });

  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * ticketsPerPage,
    currentPage * ticketsPerPage
  );

  const openCount = tickets.filter(t => t.status === 'open').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved').length;

  const openReplyModal = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setReplyText('');
    setReplyModalOpen(true);
  };

  const closeReplyModal = () => {
    setReplyModalOpen(false);
    setSelectedTicket(null);
    setReplyText('');
  };

  const handleReply = async () => {
    if (!selectedTicket || !replyText.trim()) return;

    try {
      setReplyLoading(true);
      await replySupportTicket(selectedTicket.id, replyText.trim(), adminEmail);
      closeReplyModal();
      await loadTickets();
    } catch (err) {
      alert(`Failed to send reply: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setReplyLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { icon: React.ReactNode; className: string; label: string }> = {
      open: { icon: <Clock size={14} />, className: 'badge-open', label: 'Open' },
      resolved: { icon: <CheckCircle size={14} />, className: 'badge-resolved', label: 'Resolved' },
    };
    const badge = badges[status] || badges.open;
    return (
      <span className={`status-badge ${badge.className}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string | null, maxLength: number = 100) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="support-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading support tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="support-page">
        <div className="error-container">
          <AlertTriangle size={48} />
          <h3>Error Loading Tickets</h3>
          <p>{error}</p>
          <button onClick={loadTickets} className="retry-button">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="page-header">
        <div className="header-title">
          <HelpCircle size={28} />
          <h1>Support Tickets</h1>
        </div>
        <button onClick={loadTickets} className="refresh-button" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div 
          className={`stat-card stat-open ${filterStatus === 'open' ? 'active' : ''}`} 
          onClick={() => { setFilterStatus('open'); setCurrentPage(1); }}
        >
          <Clock size={24} />
          <div className="stat-info">
            <span className="stat-value">{openCount}</span>
            <span className="stat-label">Open</span>
          </div>
        </div>
        <div 
          className={`stat-card stat-resolved ${filterStatus === 'resolved' ? 'active' : ''}`} 
          onClick={() => { setFilterStatus('resolved'); setCurrentPage(1); }}
        >
          <CheckCircle size={24} />
          <div className="stat-info">
            <span className="stat-value">{resolvedCount}</span>
            <span className="stat-label">Resolved</span>
          </div>
        </div>
        <div 
          className={`stat-card stat-total ${filterStatus === 'all' ? 'active' : ''}`} 
          onClick={() => { setFilterStatus('all'); setCurrentPage(1); }}
        >
          <HelpCircle size={24} />
          <div className="stat-info">
            <span className="stat-value">{tickets.length}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="tickets-table-container">
        {paginatedTickets.length === 0 ? (
          <div className="empty-state">
            <HelpCircle size={48} />
            <h3>No Tickets Found</h3>
            <p>There are no {filterStatus !== 'all' ? filterStatus : ''} support tickets at the moment.</p>
          </div>
        ) : (
          <table className="tickets-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Subject</th>
                <th>Message</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTickets.map((ticket) => (
                <tr key={ticket.id} className={ticket.status === 'open' ? 'row-open' : ''}>
                  <td className="cell-user">
                    <div className="user-info">
                      <User size={16} />
                      <div>
                        <div className="user-name">{ticket.name}</div>
                        <div className="user-email">{ticket.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="cell-subject">{ticket.subject}</td>
                  <td className="cell-message">{truncateText(ticket.message, 80)}</td>
                  <td>{getStatusBadge(ticket.status)}</td>
                  <td className="cell-date">{formatDate(ticket.created_at)}</td>
                  <td className="cell-actions">
                    {ticket.status === 'open' ? (
                      <button
                        className="action-btn btn-reply"
                        onClick={() => openReplyModal(ticket)}
                        title="Reply to Ticket"
                      >
                        <MessageSquare size={14} />
                        Reply
                      </button>
                    ) : (
                      <button
                        className="action-btn btn-view"
                        onClick={() => openReplyModal(ticket)}
                        title="View Reply"
                      >
                        <Mail size={14} />
                        View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {filteredTickets.length > ticketsPerPage && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="page-btn"
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Reply Modal */}
      {replyModalOpen && selectedTicket && (
        <div className="modal-overlay" onClick={closeReplyModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedTicket.status === 'open' ? 'Reply to Ticket' : 'Ticket Details'}</h2>
              <button className="modal-close-btn" onClick={closeReplyModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="ticket-details">
                <div className="detail-row">
                  <span className="detail-label">From:</span>
                  <span className="detail-value">{selectedTicket.name} ({selectedTicket.email})</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Subject:</span>
                  <span className="detail-value">{selectedTicket.subject}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Received:</span>
                  <span className="detail-value">{formatDate(selectedTicket.created_at)}</span>
                </div>
              </div>

              <div className="message-section">
                <h3>User Message</h3>
                <div className="message-box user-message">
                  {selectedTicket.message}
                </div>
              </div>

              {selectedTicket.status === 'resolved' && selectedTicket.reply && (
                <div className="message-section">
                  <h3>Your Reply</h3>
                  <div className="message-box admin-reply">
                    {selectedTicket.reply}
                  </div>
                  <div className="reply-meta">
                    Replied by {selectedTicket.replied_by} on {formatDate(selectedTicket.replied_at)}
                  </div>
                </div>
              )}

              {selectedTicket.status === 'open' && (
                <div className="reply-section">
                  <h3>Your Reply</h3>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your response here..."
                    rows={6}
                    className="reply-textarea"
                  />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeReplyModal}>
                {selectedTicket.status === 'open' ? 'Cancel' : 'Close'}
              </button>
              {selectedTicket.status === 'open' && (
                <button 
                  className="btn-send" 
                  onClick={handleReply}
                  disabled={!replyText.trim() || replyLoading}
                >
                  {replyLoading ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Reply
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
